'use client'

import { useEffect, useMemo } from 'react'
import { Divider } from '@vinted/web-ui'

import { LanguageSelector } from '@marketplace-web/language/language-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  transformCatalogNavigationDtoList,
  CatalogNavigationDto,
} from '@marketplace-web/catalog/catalog-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { LanguageDto } from '@marketplace-web/language/language-data'
import {
  useAbTest,
  useTrackAbTest,
  useTrackAbTestCallback,
} from '@marketplace-web/feature-flags/ab-tests-data'
import { AccountLinks, AccountLinksWithIcons } from '@marketplace-web/user-menu/user-menu-feature'

import CategoriesSection from './CategoriesSection'
import LoginSection from './LoginSection'
import OtherSection from './OtherSection'

type Props = {
  catalogTree: Array<CatalogNavigationDto>
  impressumUrl: string | null
  isBusinessAccountLinksVisible: boolean
  initialLanguages: Array<LanguageDto>
}

const SideNavigation = ({
  catalogTree,
  impressumUrl,
  isBusinessAccountLinksVisible,
  initialLanguages,
}: Props) => {
  const { user } = useSession()
  const trackAbTest = useTrackAbTestCallback()

  const transformedTree = useMemo(
    () => transformCatalogNavigationDtoList(catalogTree),
    [catalogTree],
  )
  const visibleTree = useMemo(
    () => transformedTree.filter(item => !item.isHidden),
    [transformedTree],
  )

  useEffect(() => {
    transformedTree.forEach(item => {
      if (item.experiment) {
        trackAbTest(item.experiment)
      }
    })
  }, [transformedTree, trackAbTest])

  const userMenuDropdownAbTest = useAbTest('user_menu_dropdown_reorder_web')
  useTrackAbTest(userMenuDropdownAbTest)

  const isUserMenuDropdOwnAbTestOn =
    userMenuDropdownAbTest && userMenuDropdownAbTest.variant !== 'off'

  return (
    <SeparatedList separator={<Divider />}>
      <LoginSection isLoggedIn={!!user} />
      <CategoriesSection categories={visibleTree} />
      {isUserMenuDropdOwnAbTestOn ? <AccountLinksWithIcons /> : <AccountLinks />}
      <LanguageSelector initialLanguages={initialLanguages} />
      <OtherSection
        impressumUrl={impressumUrl}
        isBusinessAccountLinksVisible={isBusinessAccountLinksVisible}
      />
    </SeparatedList>
  )
}

export default SideNavigation
